import { dcos, dsin, fbm2 } from '@vm/shared';
import { M } from '../palette';
import { blocks, MapBuilder, planks, smoothField, solid, symPoint, symYaw, yawToward, type Brush } from './builder';
import { emptyMeta, type CoreRegion, type MapDef, type MapMeta } from './types';

const SX = 960;
const SZ = 640;
const G = 32;
const SYM = 'mirrorX' as const;
/** Curtain wall height (the wall-walk surface) and thickness. */
const WH = 60;
const WT = 12;
/** Castle footprint (west castle; the east one is its mirror). */
const CX0 = 80;
const CX1 = 320;
const CZ0 = 160;
const CZ1 = 480;
/** Moat: water surface (first air) and bed. */
const MOAT_TOP = 21;
const MOAT_BED = 12;
/** Demolition core (west, team 0): a 1.6 m crystal inside the keep's vault. */
const CORE: CoreRegion = { team: 0, x0: 157, y0: G + 6, z0: 312, x1: 173, y1: G + 22, z1: 328 };

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

function terrain(b: MapBuilder): void {
  const h = new Int16Array(SX * SZ);
  const surface = new Uint8Array(SX * SZ);
  const hills = smoothField(SX, SZ, 4, (x, z) => fbm2(x * 0.012, z * 0.012, 301, 3));
  const tint = smoothField(SX, SZ, 4, (x, z) => fbm2(x * 0.04, z * 0.04, 302, 2));
  const grass = [M('grass_2'), M('grass_1'), M('grass_3')];
  for (let z = 0; z < SZ; z++)
    for (let x = 0; x < SX; x++) {
      const mx = x < SX / 2 ? x : SX - 1 - x;
      // Rolling field between the castles, flat around the castles and near the middle road.
      const castle = Math.max(
        clamp01(1 - Math.max(40 - mx, mx - 380, 110 - z, z - 530) / 30),
        clamp01(1 - Math.max(280 - z, z - 360) / 24),
      );
      const edge = clamp01((60 - Math.min(z, SZ - 1 - z)) / 40);
      const hill = Math.max(0, hills(mx, z) - 0.45) * 40 * (1 - castle) + edge * 10;
      h[x + z * SX] = G + Math.floor(hill / 3) * 3;
      surface[x + z * SX] = grass[Math.min(2, Math.floor(tint(mx, z) * 3.3))];
    }
  b.heightfield({ height: h, surface, sub: [M('dirt_1'), M('dirt_2')], subDepth: 4, deep: M('rock_1'), quantize: 2 });
}

/**
 * Octagonal tower from `y0`, with a room at wall-walk level and battlements. The room is a cross of two 3.0 m wide bands, 5.8 m long, too short for a straight
 * 34 degree flight (it needs 8.4 m with its 2.0 m foot and 1.0 m landing), so the stairs are a U-turn along the north-south band: lane A climbs from a foot
 * beside the wall-walk door at one end to a landing at the other, lane B climbs back beside it to the roof. The lanes sit on the side of the band away from the
 * two doors (`sx` / `sz` say which way the castle's interior lies from this tower), and the roof opening covers lane B and the landing.
 */
function tower(b: MapBuilder, cx: number, cz: number, r: number, y0: number, top: number, stone: Brush, trim: Brush, sx: number, sz: number): void {
  const room = G + WH;
  const rise = top - room;
  b.octagon(cx, cz, y0, top, r, stone);
  b.octagon(cx, cz, top - 1, top, r - 1, trim);
  b.octagon(cx, cz, room, top - 2, r - 5, 0);
  const lane = 10;
  // Lane A: 1.0 m wide, on the side away from the east/west door; its foot is the 2.0 m of floor at the end of the band where the north/south door is.
  const ax = sx > 0 ? cx - 15 : cx + 5;
  const az = cz + sz * 9;
  const [fa, fb, land] = b.switchbackFlight(ax, room, az, sz > 0 ? 'n' : 's', lane, rise, trim, sx > 0 ? 'a' : 'b', 3);
  // Roof opening over both lanes and the landing: head room for everything you stand on.
  b.openAbove(top - 3, top, [fa, fb, land], 4);
  for (let a = 0; a < 16; a += 2) {
    const ang = (a / 16) * Math.PI * 2;
    const dx = Math.round(dcos(ang) * (r - 2));
    const dz = Math.round(dsin(ang) * (r - 2));
    b.box(cx + dx - 2, top, cz + dz - 2, cx + dx + 3, top + 8, cz + dz + 3, trim);
  }
}

/** Wooden siege engine with a throwing arm (a tall, breakable prop). */
function trebuchet(b: MapBuilder, x: number, z: number): void {
  const wood = M('log_1');
  const dark = M('log_2');
  b.box(x, G, z, x + 40, G + 4, z + 4, dark);
  b.box(x, G, z + 20, x + 40, G + 4, z + 24, dark);
  for (const zz of [z, z + 20]) {
    b.line(x + 6, G + 4, zz + 1, x + 20, G + 44, zz + 1, wood, 3);
    b.line(x + 34, G + 4, zz + 1, x + 20, G + 44, zz + 1, wood, 3);
  }
  b.box(x + 18, G + 43, z, x + 23, G + 47, z + 24, dark);
  b.line(x + 20, G + 45, z + 11, x - 14, G + 72, z + 11, wood, 3);
  b.line(x + 20, G + 45, z + 11, x + 32, G + 36, z + 11, wood, 3);
  b.box(x + 28, G + 22, z + 6, x + 40, G + 36, z + 18, blocks([M('stone_4')], 3, 0));
  for (const [dx, dz] of [[2, -2], [34, -2], [2, 24], [34, 24]]) b.box(x + dx, G, z + dz, x + dx + 5, G + 5, z + dz + 3, M('planks_dark_1'));
}

function westCastle(b: MapBuilder): void {
  const stone = blocks([M('stone_1')], 3, 0);
  const stone2 = blocks([M('stone_3')], 3, 1);
  const trim = blocks([M('stone_2')], 3, 2);
  const cob = blocks([M('cobble_1')], 3, 3);

  // Moat ring, then the courtyard floor.
  for (const [x0, z0, x1, z1] of [
    [CX0 - 34, CZ0 - 34, CX1 + 34, CZ0],
    [CX0 - 34, CZ1, CX1 + 34, CZ1 + 34],
    [CX0 - 34, CZ0, CX0, CZ1],
    [CX1, CZ0, CX1 + 34, CZ1],
  ]) {
    b.clear(x0, MOAT_BED, z0, x1, G + 20, z1);
    b.box(x0, MOAT_BED - 1, z0, x1, MOAT_BED, z1, M('mud'));
    b.box(x0, MOAT_BED, z0, x1, MOAT_TOP, z1, M('water'));
  }
  b.box(CX0, G - 1, CZ0, CX1, G, CZ1, cob);

  // Curtain walls with a wall-walk and battlements.
  const top = G + WH;
  b.box(CX0, MOAT_BED, CZ0, CX1, top, CZ0 + WT, stone);
  b.box(CX0, MOAT_BED, CZ1 - WT, CX1, top, CZ1, stone);
  b.box(CX0, MOAT_BED, CZ0, CX0 + WT, top, CZ1, stone);
  b.box(CX1 - WT, MOAT_BED, CZ0, CX1, top, CZ1, stone);
  b.box(CX0, top - 1, CZ0, CX1, top, CZ1, trim);
  b.clear(CX0 + WT, top - 1, CZ0 + WT, CX1 - WT, top, CZ1 - WT);
  b.crenellate(CX0, CZ0, CX1, CZ1, top, 9, 3, trim, 5, 4);
  // Arrow slits along the outer faces.
  for (let z = CZ0 + 40; z < CZ1 - 40; z += 30) {
    b.clear(CX0, G + 20, z, CX0 + WT, G + 34, z + 2);
    b.clear(CX1 - WT, G + 20, z, CX1, G + 34, z + 2);
  }

  // Gatehouse on the east wall facing the field, with flanking towers and a raised portcullis.
  const gz0 = 300;
  const gz1 = 340;
  for (const [z0, z1] of [[272, gz0], [gz1, 368]]) {
    b.box(CX1 - 22, MOAT_BED, z0, CX1 + 10, top + 20, z1, stone2);
    b.crenellate(CX1 - 22, z0, CX1 + 10, z1, top + 20, 8, 3, trim, 5, 4);
    b.box(CX1 - 22, top + 19, z0, CX1 + 10, top + 20, z1, trim);
    b.clear(CX1 - 18, top, z0 + 4, CX1 + 6, top + 18, z1 - 4);
    // The wall-walk runs straight through the gate tower's room.
    b.clear(CX1 - WT + 1, top, z0, CX1 - 1, top + 18, z1);
  }
  b.clear(CX1 - 22, G, gz0, CX1 + 10, G + 40, gz1);
  for (let dz = 0; dz < gz1 - gz0; dz++) {
    const t = (dz + 0.5) / (gz1 - gz0);
    const arch = Math.round(Math.sqrt(Math.max(0, 1 - (2 * t - 1) * (2 * t - 1))) * 8);
    b.clear(CX1 - 22, G + 40, gz0 + dz, CX1 + 10, G + 40 + arch, gz0 + dz + 1);
  }
  for (let z = gz0 + 2; z < gz1; z += 4) b.box(CX1 - 6, G + 36, z, CX1 - 4, G + 40, z + 1, M('steel_dark_1'));
  b.box(CX1 - 6, G + 38, gz0, CX1 - 4, G + 40, gz1, M('steel_dark_1'));
  b.box(CX1 - 20, G, gz0, CX1 - 8, G + 34, gz0 + 2, planks([M('planks_dark_1')], 0));
  b.box(CX1 - 20, G, gz1 - 2, CX1 - 8, G + 34, gz1, planks([M('planks_dark_1')], 0));
  b.box(CX1 + 6, G + 44, gz0 + 8, CX1 + 8, G + 64, gz1 - 8, M('team_red'));
  // Lowered drawbridge over the moat, with chains back to the gatehouse.
  b.box(CX1, G - 3, gz0 + 2, CX1 + 38, G, gz1 - 2, planks([M('planks_4')], 0));
  for (const z of [gz0 + 2, gz1 - 3]) b.line(CX1 + 9, G + 38, z, CX1 + 36, G, z, M('steel_dark_2'));
  // Explosive powder kegs by the gate.
  b.tntBarrel(CX1 - 34, G, gz0 - 18);
  b.tntBarrel(CX1 - 34, G, gz1 + 18);

  // Corner towers standing in the moat.
  for (const [cx, cz] of [[CX0, CZ0], [CX1, CZ0], [CX0, CZ1], [CX1, CZ1]]) {
    tower(b, cx, cz, 34, MOAT_BED, G + 96, stone2, trim, cx === CX0 ? 1 : -1, cz === CZ0 ? 1 : -1);
    // Doors from both wall-walks into the tower room.
    const wx = cx === CX0 ? CX0 + WT / 2 : CX1 - WT / 2;
    const wz = cz === CZ0 ? CZ0 + WT / 2 : CZ1 - WT / 2;
    const alongZ = cz === CZ0 ? [cz + 17, cz + 31] : [cz - 31, cz - 17];
    const alongX = cx === CX0 ? [cx + 17, cx + 31] : [cx - 31, cx - 17];
    b.clear(wx - 5, top, alongZ[0], wx + 5, top + 22, alongZ[1]);
    b.clear(alongX[0], top, wz - 5, alongX[1], top + 22, wz + 5);
  }
  // Flags on the outer towers, planted beside the stairwell.
  for (const [cx, cz] of [[CX0, CZ0], [CX0, CZ1]]) {
    const fz = cz + (cz === CZ0 ? -14 : 14);
    b.box(cx - 15, G + 96, fz - 1, cx - 13, G + 126, fz + 1, M('log_2'));
    b.box(cx - 13, G + 112, fz, cx + 6, G + 124, fz + 1, M('cloth_red'));
  }

  // Stairs from the courtyard up to the wall-walk (north and south walls).
  b.climbEnding(CX0 + 120, G, CZ0 + WT, 'e', 14, WH, stone2);
  b.climbEnding(CX0 + 120, G, CZ1 - WT - 14, 'e', 14, WH, stone2);

  // Keep: two floors of heavy stone, battlements, and the core vault on the ground floor.
  b.building({
    x0: 110, x1: 220, z0: 250, z1: 390, y0: G - 1, floors: 2, floorH: 32, wallT: 4, wall: stone, trim, roofStyle: 'flat', parapet: 0, roofAccess: true,
    floor: planks([M('planks_dark_1')], 0), windows: { w: 3, h: 10, sill: 12, spacing: 16, glass: null },
    doors: [{ side: 'e', w: 14, h: 26 }, { side: 'n', w: 12 }, { side: 's', w: 12 }],
  });
  const keepTop = G - 1 + 64 + 2;
  b.crenellate(110, 250, 220, 390, keepTop, 8, 3, trim, 5, 4);
  // (Roof access is a roofAccess flight of the building above.)
  // Vault: thick walls around the core with a barred window-door facing the gate side.
  b.box(133, G, 288, 197, G + 30, 352, blocks([M('obsidian')], 3, 0));
  b.clear(145, G, 300, 185, G + 26, 340);
  b.box(145, G - 1, 300, 185, G, 340, M('marble_1'));
  b.clear(185, G, 314, 197, G + 22, 326);
  for (let z = 314; z < 326; z += 3) b.box(191, G, z, 193, G + 22, z + 1, M('steel_dark_1'));
  b.box(191, G + 20, 314, 193, G + 22, 326, M('steel_dark_1'));
  b.box(CORE.x0 - 3, G, CORE.z0 - 3, CORE.x1 + 3, CORE.y0, CORE.z1 + 3, M('gold'));
  b.box(CORE.x0, CORE.y0, CORE.z0, CORE.x1, CORE.y1, CORE.z1, M('core_red'));
  for (const [x, z] of [[146, 301], [183, 301], [146, 338], [183, 338]]) b.box(x, G, z, x + 1, G + 26, z + 1, M('team_red_glow'));
  b.box(196, G + 24, 316, 198, G + 30, 324, M('team_red'));

  // Courtyard life: stable, well, forge, crates, hay and banners.
  b.box(CX0 + WT, G + 26, 180, CX0 + 60, G + 28, 240, planks([M('planks_2')], 0));
  for (const z of [180, 208, 237]) b.box(CX0 + 56, G, z, CX0 + 59, G + 26, z + 3, M('log_1'));
  b.haybale(CX0 + 20, G, 190);
  b.haybale(CX0 + 20, G, 210);
  b.haybale(CX0 + 20, G + 8, 200);
  b.cylinder(262, 222, G, G + 8, 8, stone2);
  b.cylinder(262, 222, G + 1, G + 8, 5.5, M('water'));
  for (const dx of [-7, 6]) b.box(262 + dx, G + 8, 221, 263 + dx, G + 26, 223, M('log_1'));
  b.box(254, G + 26, 216, 271, G + 29, 229, M('roof_red_1'));
  b.box(240, G, 420, 262, G + 12, 440, blocks([M('stone_4')], 3, 0));
  b.box(244, G + 12, 424, 258, G + 13, 436, M('neon_orange'));
  b.box(248, G + 12, 438, 254, G + 40, 444, blocks([M('brick_gray')], 3, 0));
  b.crate(200, G, 420);
  b.crate(210, G, 430, 8);
  b.barrel(230, G, 190, M('planks_4'));
  b.barrel(238, G, 196, M('planks_4'));
  for (const z of [200, 440]) b.box(CX0 + WT, G + 30, z, CX0 + WT + 1, G + 56, z + 16, M('team_red'));
}

function field(b: MapBuilder): void {
  const stone = blocks([M('stone_1')], 3, 5);
  trebuchet(b, 380, 190);
  trebuchet(b, 390, 420);
  // Ruined watchtower in the middle of the field (west half; the mirror completes it).
  b.octagon(480, 320, G, G + 70, 24, stone);
  b.octagon(480, 320, G, G + 64, 18, 0);
  b.clear(456, G, 312, 470, G + 24, 328);
  b.box(456, G + 30, 300, 470, G + 40, 312, 0);
  for (const [x, hh] of [[458, 80], [466, 90], [474, 76]]) b.box(x, G + 70, 298, x + 4, G + hh, 302, stone);
  b.box(462, G + 40, 305, 478, G + 42, 336, planks([M('planks_dark_2')], 0));
  // Hedgerows, hay and boulders for cover across the field.
  for (const [x, z, len] of [[360, 130, 60], [410, 300, 40], [360, 500, 60], [430, 150, 30], [430, 470, 30]] as const) {
    for (let i = 0; i < len; i += 6) b.bush(x + i, b.surfaceY(x + i, z, 80), z, 6, M('leaves_1'));
  }
  for (const [x, z] of [[420, 260], [428, 372], [370, 330]]) b.haybale(x, b.surfaceY(x, z, 80), z);
  for (const [x, z, r] of [[400, 100, 9], [440, 560, 10], [352, 290, 7]] as const) b.rock(x, b.surfaceY(x, z, 80) - 2, z, r, M('stone_2'));
  for (const [x, z] of [[20, 60], [30, 590], [200, 60], [230, 590], [420, 40], [440, 610]]) b.leafyTree(x, b.surfaceY(x, z, 80), z, 42, 13, M('leaves_2'));
  // Dirt road from the drawbridge to the middle.
  b.box(CX1 + 38, G - 1, 306, 480, G, 334, solid([M('dirt_3')]));
}

function build(b: MapBuilder): MapMeta {
  terrain(b);
  field(b);
  westCastle(b);
  b.symmetrize(SYM);

  // Metadata ------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, SX / 20, SZ / 20);
  const team0: [number, number][] = [
    [15, 20], [17.5, 20], [15, 22.5], [17.5, 22.5], [19.8, 21.2], [15, 42], [17.5, 42], [15, 44.5], [17.5, 44.5], [19.2, 43.2],
  ];
  for (const [x, z] of team0) {
    const yaw = yawToward(x, z, 30, 32);
    meta.spawns.push({ team: 0, x, y: gy, z, yaw });
    const [mx, mz] = symPoint(SYM, SX / 10, SZ / 10, x, z);
    meta.spawns.push({ team: 1, x: mx, y: gy, z: mz, yaw: symYaw(SYM, yaw) });
  }
  const ffa: [number, number][] = [
    [36, 8], [60, 8], [36, 56], [60, 56], [40, 24], [56, 24], [40, 40], [56, 40], [44, 12.5], [52, 12.5], [44, 50], [52, 50],
    [23.5, 23], [72.5, 23], [23.5, 41], [72.5, 41],
  ];
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x, y: b.standAt(x, z, 6), z, yaw: yawToward(x, z, 48, 32) });
  meta.cores.push({ ...CORE });
  meta.cores.push({ team: 1, x0: SX - CORE.x1, y0: CORE.y0, z0: CORE.z0, x1: SX - CORE.x0, y1: CORE.y1, z1: CORE.z1 });
  meta.flags.push({ team: 0, x: 25, y: gy, z: 32 }, { team: 1, x: 71, y: gy, z: 32 });
  meta.points.push({ id: 'A', name: 'West Trebuchet', x: 38.2, y: b.standAt(38.2, 26, 6), z: 26, r: 3.5 });
  meta.points.push({ id: 'B', name: 'Old Watchtower', x: 48, y: gy, z: 32, r: 3.5 });
  meta.points.push({ id: 'C', name: 'East Trebuchet', x: 57.8, y: b.standAt(57.8, 26, 6), z: 26, r: 3.5 });
  // Pickups stand on the ground (looked up from 4.5 m so a roof or canopy above them is ignored). The first health pack used to sit at the
  // well in (26, 22.5), where the lookup found the well's red canopy 2.9 m up: nobody could reach it.
  for (const [x, z, kind] of [[28.6, 22.5, 'health'], [26, 41.5, 'health'], [34.5, 32, 'armor'], [44, 32, 'health']] as const) {
    meta.pickups.push({ kind, x, y: b.standAt(x, z, 4.5), z });
    const [mx, mz] = symPoint(SYM, SX / 10, SZ / 10, x, z);
    meta.pickups.push({ kind, x: mx, y: b.standAt(mx, mz, 4.5), z: mz });
  }
  return meta;
}

export const castleClash: MapDef = {
  id: 'castle_clash',
  name: 'Castle Clash',
  description: 'Two castles glare across a meadow. Lower the drawbridge, breach the walls and shatter the enemy crystal.',
  size: [SX, 192, SZ],
  modes: ['demolition', 'siege', 'ctf', 'tdm', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 1215,
  version: 2,
  theme: {
    skyTop: 0x4e8ad8,
    skyHorizon: 0xd6e8f6,
    fog: 0xc8dcee,
    fogNear: 50,
    fogFar: 170,
    sun: [0.3, 0.8, 0.45],
    sunColor: 0xfff2dc,
    sunIntensity: 1.0,
    ambient: 0xbcd0f0,
    ambientIntensity: 0.56,
    outside: 0x5a9a3a,
    music: 'castle',
  },
  build,
};
